import { useCallback, useEffect, useState } from "react";
import { AUTH_CONFIG, type AuthConfig } from "@/lib/auth-config";

/**
 * The three states the Password protection card can render:
 *  - "loading" → we haven't confirmed the auth-config values yet.
 *  - "ready"   → validated config is available for display.
 *  - "error"   → config could not be loaded or is malformed.
 */
export type AuthConfigStatus = "loading" | "ready" | "error";

export type UseAuthConfigResult =
  | { status: "loading"; config: null; error: null; retry: () => void }
  | { status: "ready"; config: AuthConfig; error: null; retry: () => void }
  | { status: "error"; config: null; error: Error; retry: () => void };

const REQUIRED_KEYS = [
  "passwordHibpEnabled",
  "anonymousSignInsEnabled",
  "autoConfirmEmail",
] as const satisfies readonly (keyof AuthConfig)[];

function validate(candidate: unknown): AuthConfig {
  if (!candidate || typeof candidate !== "object") {
    throw new Error("Auth configuration is unavailable.");
  }
  const record = candidate as Record<string, unknown>;
  for (const key of REQUIRED_KEYS) {
    if (typeof record[key] !== "boolean") {
      throw new Error(
        `Auth configuration is missing or malformed: expected boolean "${key}".`,
      );
    }
  }
  return candidate as AuthConfig;
}

/**
 * Reads the project's static auth-config mirror and exposes it with an
 * explicit loading + error surface, so consumers can render skeletons and
 * recovery UI instead of silently showing stale/undefined values if the
 * shape ever drifts.
 */
export function useAuthConfig(): UseAuthConfigResult {
  const [state, setState] = useState<
    | { status: "loading" }
    | { status: "ready"; config: AuthConfig }
    | { status: "error"; error: Error }
  >({ status: "loading" });

  const load = useCallback(() => {
    setState({ status: "loading" });
    // Deferred to a microtask so the "loading" state is observable and future
    // async sources (e.g. an admin RPC) can drop in without touching callers.
    queueMicrotask(() => {
      try {
        const config = validate(AUTH_CONFIG);
        setState({ status: "ready", config });
      } catch (err) {
        setState({
          status: "error",
          error: err instanceof Error ? err : new Error(String(err)),
        });
      }
    });
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  if (state.status === "loading") {
    return { status: "loading", config: null, error: null, retry: load };
  }
  if (state.status === "error") {
    return { status: "error", config: null, error: state.error, retry: load };
  }
  return { status: "ready", config: state.config, error: null, retry: load };
}
